import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from "../../../app/Context/auth";

// --- Funções de formatação (mantidas) ---
const handleValorChange = (e, setState, maxDecimals = 2) => {
    let valor = e.target.value;
    valor = valor.replace(/[^0-9,.]/g, '');
    valor = valor.replace(',', '.');
    const partes = valor.split('.');
    if (partes.length > 2) {
        valor = partes[0] + '.' + partes.slice(1).join('');
    }
    if (partes[1] && partes[1].length > maxDecimals) {
        valor = `${partes[0]}.${partes[1].substring(0, maxDecimals)}`;
    }
    setState(valor);
};
const formatarAoPerderFoco = (valor, setState, decimals = 2) => {
    if (valor === '' || valor === null || valor === undefined) {
        setState('');
        return;
    };
    const numero = parseFloat(valor);
    if (isNaN(numero)) {
        setState('');
        return;
    }
    setState(numero.toFixed(decimals));
};
const formatarValorMonetario = (valor) => {
    const numero = parseFloat(valor);
    if (isNaN(numero)) return 'R$ 0,00';
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};


function ProdutoForm({ initialData = {}, onSubmit, isEditing = false, loading: loadingSubmit }) {
    const { currentUser, appUserData } = useAuth();
    const { id: produtoIdParam } = useParams();
    const produtoId = isEditing ? produtoIdParam : null;

    const modulos = appUserData?.modulos || [];
    const hasModuloProducao = modulos.includes(3);


    // --- Estados do Formulário Principal (mantidos e ajustados) ---
    const [codigo, setCodigo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [marcaId, setMarcaId] = useState('');
    const [unidadeMedidaId, setUnidadeMedidaId] = useState('');
    const [fornecedorId, setFornecedorId] = useState('');
    const [valorCompra, setValorCompra] = useState('');
    const [valorVenda, setValorVenda] = useState('');
    const [tipo, setTipo] = useState('Atacado');
    const [status, setStatus] = useState('Ativo');
    const [observacoes, setObservacoes] = useState('');

    const [fornecedores, setFornecedores] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [unidadesMedida, setUnidadesMedida] = useState([]);
    const [gradesDisponiveis, setGradesDisponiveis] = useState([]);
    const [controlarCores, setControlarCores] = useState(false);
    const [colorsList, setColorsList] = useState([]);
    const [currentColor, setCurrentColor] = useState('');
    const [controlarGrade, setControlarGrade] = useState(false);
    const [gradeIdParaAdicionar, setGradeIdParaAdicionar] = useState('');
    const [gradesVinculadas, setGradesVinculadas] = useState([]);

    // --- Estados para Múltiplos Fornecedores (NOVO) ---
    const [fornecedoresVinculados, setFornecedoresVinculados] = useState([]);
    const [fornecedorExtraId, setFornecedorExtraId] = useState('');
    const [valorCompraExtra, setValorCompraExtra] = useState('');
    const [valorVendaExtra, setValorVendaExtra] = useState(''); // NOVO

    // --- Estados da Aba Ficha Técnica (NOVOS) ---
    const [activeTab, setActiveTab] = useState('dados');
    const [corFichaTecnica, setCorFichaTecnica] = useState('');
    const [fichaTecnicaItens, setFichaTecnicaItens] = useState([]);
    const [loadingFicha, setLoadingFicha] = useState(false);
    const [etapasProducao, setEtapasProducao] = useState([]);
    const [insumosDisponiveis, setInsumosDisponiveis] = useState([]);
    const [etapaSelecionadaId, setEtapaSelecionadaId] = useState('');
    const [insumoSelecionadoId, setInsumoSelecionadoId] = useState('');
    const [quantidadeInsumo, setQuantidadeInsumo] = useState('');
    const [insumoCorSelecionada, setInsumoCorSelecionada] = useState('');


    // --- Hook para chamadas API (NOVO) ---
    const apiCall = useCallback(async (url, method = 'GET', data = null) => {
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const config = {
            method,
            headers: { 'Authorization': `Bearer ${token}` }
        };
        if (data && method !== 'GET') {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(data);
        }
        const response = await fetch(`${API_URL}${url}`, config);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Falha na requisição para ${url} (${response.status})`);
        }
        if (response.status === 204) return null;
        return response.json();
    }, [currentUser]);

    // --- Estado de Dirty (Modificado) ---
    const [isDirty, setIsDirty] = useState(false);

    // Função auxiliar para comparação profunda simples (suficiente para os dados do form)
    const deepEqual = (obj1, obj2) => {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    };

    // Monta o objeto de dados atual para comparação
    const getCurrentFormData = useCallback(() => {
        return {
            codigo,
            descricao,
            marca_id: marcaId ? parseInt(marcaId) : null,
            unidade_medida_id: unidadeMedidaId ? parseInt(unidadeMedidaId) : null,
            fornecedor_id: fornecedorId ? parseInt(fornecedorId) : null,
            valor_compra: parseFloat(String(valorCompra).replace(',', '.')) || 0,
            valor_venda: parseFloat(String(valorVenda).replace(',', '.')) || 0,
            tipo,
            status,
            observacoes,
            colorsList: colorsList.sort(), // Ordenar para garantir igualdade
            gradesList: gradesVinculadas.map(g => ({ id: g.id })).sort((a, b) => a.id - b.id), // Normalizar grades
            fornecedoresList: fornecedoresVinculados.map(f => ({
                fornecedor_id: f.fornecedor_id,
                nome_fornecedor: f.nome || f.nome_fornecedor, // Normalizar nome se vier diferente
                valor_compra: f.valor_compra,
                valor_venda: f.valor_venda
            })).sort((a, b) => a.fornecedor_id - b.fornecedor_id)
        };
    }, [codigo, descricao, marcaId, unidadeMedidaId, fornecedorId, valorCompra, valorVenda, tipo, status, observacoes, colorsList, gradesVinculadas, fornecedoresVinculados]);

    // Efeito para verificar alterações
    useEffect(() => {
        // Normalizar initialData para comparar com getCurrentFormData
        const normalizedInitial = {
            codigo: initialData.codigo || '',
            descricao: initialData.descricao || '',
            marca_id: initialData.marca_id || null,
            unidade_medida_id: initialData.unidade_medida_id || null,
            fornecedor_id: initialData.fornecedor_id || null,
            valor_compra: initialData.valor_compra ? parseFloat(initialData.valor_compra) : 0,
            valor_venda: initialData.valor_venda ? parseFloat(initialData.valor_venda) : 0,
            tipo: initialData.tipo || 'Atacado',
            status: initialData.status || 'Ativo',
            observacoes: initialData.observacoes || '',
            colorsList: (initialData.colorsList || []).sort(),
            gradesList: (initialData.gradesList || []).map(g => ({ id: g.id })).sort((a, b) => a.id - b.id),
            fornecedoresList: (initialData.fornecedoresList || []).map(f => ({
                fornecedor_id: f.fornecedor_id,
                nome_fornecedor: f.nome_fornecedor,
                valor_compra: f.valor_compra ? parseFloat(f.valor_compra) : 0,
                valor_venda: f.valor_venda ? parseFloat(f.valor_venda) : 0
            })).sort((a, b) => a.fornecedor_id - b.fornecedor_id)
        };

        const current = getCurrentFormData();

        // Ajustes finos para comparar valores numéricos e strings
        const isDiff = !deepEqual(normalizedInitial, current);
        setIsDirty(isDiff);

    }, [getCurrentFormData, initialData]);


    // Efeito para popular dados principais
    useEffect(() => {
        setCodigo(initialData.codigo || '');
        setDescricao(initialData.descricao || '');
        setMarcaId(initialData.marca_id || '');
        setUnidadeMedidaId(initialData.unidade_medida_id || '');
        setFornecedorId(initialData.fornecedor_id || '');
        setValorCompra(initialData.valor_compra ? String(initialData.valor_compra) : '');
        setValorVenda(initialData.valor_venda ? String(initialData.valor_venda) : '');
        setTipo(initialData.tipo || 'Atacado');
        setStatus(initialData.status || 'Ativo');
        setObservacoes(initialData.observacoes || '');

        const initialColors = initialData.colorsList || [];
        setColorsList(initialColors);
        setControlarCores(initialColors.length > 0);
        if (isEditing && initialColors.length > 0 && !corFichaTecnica) {
            setCorFichaTecnica(initialColors[0]);
        }

        // --- INÍCIO DA CORREÇÃO ---
        const gradesIniciaisIncompletas = initialData.gradesList || [];
        setControlarGrade(gradesIniciaisIncompletas.length > 0);

        if (gradesIniciaisIncompletas.length > 0 && gradesDisponiveis.length > 0) {
            const gradesCompletas = gradesIniciaisIncompletas.map(gradeIncompleta => {
                const gradeCompleta = gradesDisponiveis.find(g =>
                    String(g.id) === String(gradeIncompleta.id)
                );
                return gradeCompleta || gradeIncompleta;
            });
            setGradesVinculadas(gradesCompletas);
        } else {
            setGradesVinculadas(gradesIniciaisIncompletas);
        }
        // --- FIM DA CORREÇÃO ---

        // --- Inicializa Fornecedores Vinculados (NOVO) ---
        if (initialData.fornecedoresList && initialData.fornecedoresList.length > 0) {
            setFornecedoresVinculados(initialData.fornecedoresList.map(f => ({
                fornecedor_id: f.fornecedor_id,
                nome: f.nome_fornecedor,
                valor_compra: f.valor_compra,
                valor_venda: f.valor_venda
            })));
        } else {
            setFornecedoresVinculados([]);
        }

        // ========== CORREÇÃO DO LINT ESTÁ AQUI ==========
    }, [initialData, isEditing, gradesDisponiveis, corFichaTecnica]);
    // ===================================================

    // Efeito para buscar listas de apoio (Fornecedores, Marcas, Unidades, Grades)
    useEffect(() => {
        if (!currentUser) return;
        const buscarListas = async () => {
            try {
                const [fornData, marcaData, unData, gradeData] = await Promise.all([
                    apiCall('/api/empresas?tipo=fornecedor'),
                    apiCall('/api/marcas'),
                    apiCall('/api/unidadesmedida'),
                    apiCall('/api/grades'),
                ]);
                setFornecedores(fornData || []);
                setMarcas(marcaData || []);
                setUnidadesMedida(unData || []);
                setGradesDisponiveis(gradeData || []);

            } catch (error) { console.error("Erro ao buscar listas de apoio:", error); }
        };
        buscarListas();
    }, [currentUser, apiCall]);

    // --- Efeitos para Aba Ficha Técnica ---
    useEffect(() => {
        if (isEditing && activeTab === 'fichaTecnica' && currentUser) {
            const buscarDadosFicha = async () => {
                setLoadingFicha(true);
                try {
                    const [etapasData, insumosData] = await Promise.all([
                        apiCall('/api/etapas-producao'),
                        apiCall('/api/insumos')
                    ]);
                    setEtapasProducao(etapasData || []);
                    setInsumosDisponiveis(insumosData || []);
                } catch (error) {
                    console.error("Erro ao buscar etapas ou insumos:", error);
                    Swal.fire("Erro", "Não foi possível carregar dados para a ficha técnica.", "error");
                } finally {
                    setLoadingFicha(false);
                }
            };
            buscarDadosFicha();
        }
    }, [activeTab, isEditing, currentUser, apiCall]);

    useEffect(() => {
        if (isEditing && produtoId && corFichaTecnica && activeTab === 'fichaTecnica' && currentUser) {
            const buscarItensFicha = async () => {
                setLoadingFicha(true);
                setFichaTecnicaItens([]);
                try {
                    const data = await apiCall(`/api/produtos/${produtoId}/ficha-tecnica?cor=${encodeURIComponent(corFichaTecnica)}`);
                    setFichaTecnicaItens(data || []);
                } catch (error) {
                    console.error(`Erro ao buscar ficha para cor ${corFichaTecnica}:`, error);
                } finally {
                    setLoadingFicha(false);
                }
            };
            buscarItensFicha();
        } else {
            setFichaTecnicaItens([]);
        }
    }, [corFichaTecnica, produtoId, isEditing, activeTab, currentUser, apiCall]);

    // --- Lógica de Manipulação do Formulário Principal ---

    const handleAddColor = () => {
        const novaCor = currentColor.trim();
        if (novaCor === '') {
            Swal.fire("Atenção", "O nome da cor não pode estar vazio.", "warning");
            return;
        }
        if (colorsList.some(cor => cor.toLowerCase() === novaCor.toLowerCase())) {
            Swal.fire("Atenção", "Esta cor já foi adicionada.", "warning");
            setCurrentColor('');
            return;
        }
        setColorsList(prev => [...prev, novaCor]);
        setCurrentColor('');
    };

    const handleRemoveColor = (corToRemove) => {
        setColorsList(prev => prev.filter(cor => cor !== corToRemove));
        if (corFichaTecnica === corToRemove) {
            setCorFichaTecnica('');
        }
    };

    const handleAddGradeToProduct = () => {
        if (!gradeIdParaAdicionar) {
            Swal.fire("Atenção", "Selecione uma grade para adicionar.", "warning");
            return;
        }

        if (gradesVinculadas.some(g => String(g.id) === String(gradeIdParaAdicionar))) {
            Swal.fire("Atenção", "Esta grade já foi vinculada.", "warning");
            setGradeIdParaAdicionar('');
            return;
        }

        const gradeObj = gradesDisponiveis.find(g => String(g.id) === String(gradeIdParaAdicionar));

        if (gradeObj) {
            setGradesVinculadas(prev => [...prev, gradeObj]);
            setGradeIdParaAdicionar('');
        } else {
            Swal.fire("Erro", "Não foi possível encontrar os detalhes da grade selecionada.", "error");
        }
    };

    const handleRemoveVinculadaGrade = (idToRemove) => {
        setGradesVinculadas(prev => prev.filter(g => String(g.id) !== String(idToRemove)));
    };

    // NOVO: Memoiza o insumo selecionado para evitar recálculos
    const insumoSelecionado = useMemo(() => {
        if (!insumoSelecionadoId) return null;
        return insumosDisponiveis.find(i => String(i.id) === String(insumoSelecionadoId));
    }, [insumoSelecionadoId, insumosDisponiveis]);

    // NOVO: Memoiza se deve mostrar o seletor de cor do insumo
    const showInsumoColor = useMemo(() => {
        return insumoSelecionado &&
            insumoSelecionado.controla_cor &&
            insumoSelecionado.cores_disponiveis &&
            insumoSelecionado.cores_disponiveis.length > 0;
    }, [insumoSelecionado]);

    // NOVO: Memoiza a sigla da unidade do insumo
    const insumoUnidadeSigla = useMemo(() => {
        if (!insumoSelecionado || !unidadesMedida.length) return '';
        const unidade = unidadesMedida.find(u => String(u.id) === String(insumoSelecionado.unidade_medida_id));
        return unidade ? unidade.sigla : '';
    }, [insumoSelecionado, unidadesMedida]);

    // --- Lógica da Ficha Técnica (NOVOS Handlers) ---
    const handleAddInsumoFicha = async () => {
        if (!etapaSelecionadaId || !insumoSelecionadoId || !quantidadeInsumo || isNaN(parseFloat(quantidadeInsumo)) || parseFloat(quantidadeInsumo) <= 0) {
            Swal.fire("Atenção", "Selecione a Etapa, o Insumo e informe uma Quantidade válida (> 0).", "warning");
            return;
        }

        if (showInsumoColor && !insumoCorSelecionada) {
            Swal.fire("Atenção", "O insumo selecionado requer que uma cor seja especificada.", "warning");
            return;
        }

        setLoadingFicha(true);
        try {
            const payload = {
                cor: corFichaTecnica,
                etapa_id: etapaSelecionadaId,
                insumo_id: insumoSelecionadoId,
                quantidade: parseFloat(quantidadeInsumo.replace(',', '.')),
                insumo_cor: showInsumoColor ? insumoCorSelecionada : null
            };

            await apiCall(`/api/produtos/${produtoId}/ficha-tecnica`, 'POST', payload);

            const data = await apiCall(`/api/produtos/${produtoId}/ficha-tecnica?cor=${encodeURIComponent(corFichaTecnica)}`);
            setFichaTecnicaItens(data || []);

            setEtapaSelecionadaId('');
            setInsumoSelecionadoId('');
            setQuantidadeInsumo('');
            setInsumoCorSelecionada('');
        } catch (error) {
            console.error("Erro ao adicionar insumo:", error);
            Swal.fire("Erro", error.message || "Não foi possível adicionar o insumo.", "error");
        } finally {
            setLoadingFicha(false);
        }
    };

    const handleRemoveInsumoFicha = async (itemId) => {
        Swal.fire({
            title: 'Remover Item?',
            text: "Confirmar a remoção deste insumo da ficha técnica?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, remover!',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoadingFicha(true);
                try {
                    await apiCall(`/api/produtos/${produtoId}/ficha-tecnica/${itemId}`, 'DELETE');
                    setFichaTecnicaItens(prev => prev.filter(item => item.id !== itemId));
                } catch (error) {
                    console.error("Erro ao remover insumo:", error);
                    Swal.fire("Erro", error.message || "Não foi possível remover o insumo.", "error");
                } finally {
                    setLoadingFicha(false);
                }
            }
        });
    };

    // Calcula custo total da ficha (NOVO)
    const custoTotalFicha = useMemo(() => {
        return fichaTecnicaItens.reduce((sum, item) => sum + parseFloat(item.custo_total || 0), 0);
    }, [fichaTecnicaItens]);


    // --- Handlers para Múltiplos Fornecedores (NOVO) ---
    const handleAddFornecedor = () => {
        if (!fornecedorExtraId) {
            Swal.fire("Atenção", "Selecione um fornecedor.", "warning");
            return;
        }

        const jaExiste = fornecedoresVinculados.some(f => String(f.fornecedor_id) === String(fornecedorExtraId));
        if (jaExiste) {
            Swal.fire("Atenção", "Este fornecedor já foi adicionado.", "warning");
            return;
        }

        // Se for o mesmo do fornecedor principal, avisa (opcional, mas boa prática)
        if (String(fornecedorExtraId) === String(fornecedorId)) {
            Swal.fire("Nota", "Este já é o fornecedor principal do produto.", "info");
            // Permite adicionar mesmo assim, caso tenha preço específico diferente?
            // Geralmente não faz sentido duplicar, mas vamos deixar adicionar se o user quiser explicitamente
            // ou poderíamos bloquear. Vamos bloquear para evitar confusão.
            Swal.fire("Atenção", "Este fornecedor já está definido como fornecedor principal.", "warning");
            return;
        }

        const fornObj = fornecedores.find(f => String(f.id) === String(fornecedorExtraId));
        if (!fornObj) return;

        const novoVinculo = {
            fornecedor_id: fornObj.id,
            nome: fornObj.nome,
            valor_compra: parseFloat(String(valorCompraExtra).replace(',', '.')) || 0,
            valor_venda: parseFloat(String(valorVendaExtra).replace(',', '.')) || 0
        };

        setFornecedoresVinculados(prev => [...prev, novoVinculo]);
        setFornecedorExtraId('');
        setValorCompraExtra('');
        setValorVendaExtra('');
    };

    const handleRemoveFornecedor = (id) => {
        setFornecedoresVinculados(prev => prev.filter(f => String(f.fornecedor_id) !== String(id)));
    };


    // Submissão do Formulário Principal 
    const handleSubmitPrincipal = (event) => {
        event.preventDefault();
        let qtdeGradePadrao = null;
        if (tipo === 'Atacado') {
            if (controlarGrade && gradesVinculadas.length > 0) {
                qtdeGradePadrao = gradesVinculadas.reduce((sum, g) => sum + (Number(g.quantidade_total) || 0), 0);
            } else if (!controlarGrade) {
                qtdeGradePadrao = 1;
            }
        }

        if (controlarGrade && tipo === 'Atacado' && gradesVinculadas.length === 0) {
            Swal.fire("Atenção", "O controle de grade está ativo, mas nenhuma grade foi adicionada ao produto.", "warning");
            return;
        }

        const formData = {
            codigo, descricao, status, tipo, observacoes,
            marcaId: marcaId || null,
            fornecedorId: fornecedorId || null,
            unidadeMedidaId: unidadeMedidaId || null,
            valorCompra: parseFloat(String(valorCompra).replace(',', '.')) || 0,
            valorVenda: parseFloat(String(valorVenda).replace(',', '.')) || 0,
            quantidade_grade: qtdeGradePadrao,
            gradesList: controlarGrade ? gradesVinculadas.map(g => ({ id: g.id })) : [],
            colorsList: controlarCores ? colorsList : [],
            fornecedoresList: Array.isArray(fornecedoresVinculados) ? fornecedoresVinculados : [], // <-- GARANTINDO ARRAY
        };
        console.log('[DEBUG] ProdutoForm submitting:', formData);
        onSubmit(formData);
    };

    // Renderização
    return (
        <form onSubmit={handleSubmitPrincipal}>
            {/* --- Sistema de Abas (NOVO) --- */}
            <ul className="nav nav-tabs mb-3" id="produtoTab" role="tablist">
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link ${activeTab === 'dados' ? 'active' : ''}`}
                        id="dados-tab" type="button" role="tab"
                        onClick={() => setActiveTab('dados')}
                    >
                        Dados Cadastrais
                    </button>
                </li>
                {isEditing && hasModuloProducao && (
                    <li className="nav-item" role="presentation">
                        <button
                            className={`nav-link ${activeTab === 'fichaTecnica' ? 'active' : ''}`}
                            id="ficha-tab" type="button" role="tab"
                            onClick={() => setActiveTab('fichaTecnica')}
                            disabled={!controlarCores || colorsList.length === 0}
                        >
                            Ficha Técnica
                        </button>
                    </li>
                )}
                {/* Aba Fornecedores (Liberada para todos no Módulo Essencial) */}
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link ${activeTab === 'fornecedores' ? 'active' : ''}`}
                        id="fornecedores-tab" type="button" role="tab"
                        onClick={() => setActiveTab('fornecedores')}
                    >
                        Fornecedores
                    </button>
                </li>
            </ul>

            {/* --- Conteúdo das Abas --- */}
            <div className="tab-content" id="produtoTabContent">
                {/* --- Aba Dados Cadastrais --- */}
                <div className={`tab-pane fade ${activeTab === 'dados' ? 'show active' : ''}`} id="dados-pane" role="tabpanel">
                    <div className="row">
                        <div className="col-md-8">
                            {/* Seção Dados Básicos */}
                            <div className="form-card"> <h5 className="form-section-header">Dados Básicos</h5>
                                <div className="row g-3">
                                    <div className="col-md-2"> <label htmlFor="inputCodigo" className="form-label-custom">Código</label> <input onChange={(e) => setCodigo(e.target.value)} type="text" className="form-control-custom" id="inputCodigo" value={codigo} required /> </div>
                                    <div className="col-md-7"> <label htmlFor="inputDescricao" className="form-label-custom">Descrição</label> <input onChange={(e) => setDescricao(e.target.value)} type="text" className="form-control-custom" id="inputDescricao" value={descricao} required /> </div>
                                    <div className="col-md-3"> <label htmlFor="selectStatus" className="form-label-custom">Status</label> <select id="selectStatus" className="form-select-custom" value={status} onChange={(e) => setStatus(e.target.value)} required> <option value="Ativo">Ativo</option> <option value="Inativo">Inativo</option> </select> </div>
                                </div>
                                <div className="row g-3 mt-2">
                                    <div className="col-md-7"> <label htmlFor="selectMarca" className="form-label-custom">Marca</label> <div className="input-group"> <select id="selectMarca" className="form-select-custom" value={marcaId} onChange={(e) => setMarcaId(e.target.value)}> <option value="">Selecione...</option> {marcas.map((marca) => (<option key={marca.id} value={marca.id}>{marca.descricao}</option>))} </select> </div> </div>
                                    <div className="col-md-5"> <label htmlFor="selectUnidadeMedida" className="form-label-custom">Un. Medida</label> <div className="input-group"> <select id="selectUnidadeMedida" className="form-select-custom" value={unidadeMedidaId} onChange={(e) => setUnidadeMedidaId(e.target.value)}> <option value="">Selecione...</option> {unidadesMedida.map((un) => (<option key={un.id} value={un.id}>{un.descricao}</option>))} </select> </div> </div>
                                </div>
                            </div>
                            {/* Seção Detalhes */}
                            <div className="form-card"> <h5 className="form-section-header">Detalhes</h5>
                                <div className="row g-3">
                                    <div className="col-md-9"> <label htmlFor="selectFornecedor" className="form-label-custom">Fornecedor</label> <select id="selectFornecedor" className="form-select-custom" value={fornecedorId} onChange={(e) => setFornecedorId(e.target.value)}> <option value="">Selecione...</option> {fornecedores.map((forn) => (<option key={forn.id} value={forn.id}>{forn.nome}</option>))} </select> </div>
                                    <div className="col-md-3"> <label htmlFor="selectTipo" className="form-label-custom">Tipo</label> <select id="selectTipo" className="form-select-custom" value={tipo} onChange={(e) => setTipo(e.target.value)} required> <option value="Atacado">Atacado</option> <option value="Varejo">Varejo</option> </select> </div>
                                </div>
                                <div className="row g-3 mt-2">
                                    <div className="col-md-6"><label htmlFor="inputValorCompra" className="form-label-custom">Valor de Compra</label><input onChange={(e) => handleValorChange(e, setValorCompra)} onBlur={() => formatarAoPerderFoco(valorCompra, setValorCompra)} type="text" className="form-control-custom" id="inputValorCompra" value={String(valorCompra).replace('.', ',')} placeholder="0,00" /></div>
                                    <div className="col-md-6"><label htmlFor="inputValorVenda" className="form-label-custom">Valor de Venda</label><input onChange={(e) => handleValorChange(e, setValorVenda)} onBlur={() => formatarAoPerderFoco(valorVenda, setValorVenda)} type="text" className="form-control-custom" id="inputValorVenda" value={String(valorVenda).replace('.', ',')} placeholder="0,00" /></div>
                                </div>
                                <div className="row g-3 mt-2"> <div className="col-12"><label htmlFor="inputObservacoes" className="form-label-custom">Observações</label><textarea className="form-control-custom" id="inputObservacoes" rows="3" value={observacoes} onChange={(e) => setObservacoes(e.target.value)}></textarea></div> </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            {/* Seção Variações */}
                            <div className="form-card"> <h5 className="form-section-header">Variações</h5>
                                <div className="form-check"> <input className="form-check-input" type="checkbox" id="checkControlarCores" checked={controlarCores} onChange={(e) => setControlarCores(e.target.checked)} /> <label className="form-check-label" htmlFor="checkControlarCores">Controlar cores</label> </div>
                                {controlarCores && (<div className="cores-section mt-3"> <label htmlFor="inputCor" className="form-label-custom">Cor</label> <div className="input-group"> <input onChange={(e) => setCurrentColor(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddColor(); } }} type="text" className="form-control-custom" id="inputCor" value={currentColor} placeholder="Ex: Azul, Vermelho..." /> <button className="btn btn-primary-custom btn-sm" type="button" onClick={handleAddColor}>Adicionar</button> </div> {Array.isArray(colorsList) && colorsList.length > 0 && (<div className="items-list mt-2"> {colorsList.map((cor) => (<span key={cor} className="badge bg-secondary me-2 mb-2 p-2"> {cor} <button typea="button" className="btn-close btn-close-white ms-2" style={{ fontSize: '0.5rem' }} onClick={() => handleRemoveColor(cor)}></button> </span>))} </div>)} </div>)}
                                <hr className="mt-3 mb-3" />
                                <div className="form-check"> <input className="form-check-input" type="checkbox" id="checkControlarGrade" checked={controlarGrade} onChange={(e) => { setControlarGrade(e.target.checked); if (!e.target.checked) setGradesVinculadas([]); }} /> <label className="form-check-label" htmlFor="checkControlarGrade">Controlar Grade</label> </div>
                                {controlarGrade && tipo === 'Atacado' && (<div className="grades-config-section mt-3"> <label className="form-label-custom">Grade</label> <div className="input-group mb-2 flex-nowrap"> <select id="selectGradeParaAdicionar" className="form-select-custom form-select-custom-sm flex-grow-1" value={gradeIdParaAdicionar} onChange={(e) => setGradeIdParaAdicionar(e.target.value)}> <option value="">Selecione a Grade...</option> {gradesDisponiveis.map((grade) => (<option key={grade.id} value={grade.id}>{grade.descricao} ({grade.quantidade_total} itens)</option>))} </select> <button className="btn btn-primary-custom btn-sm" type="button" onClick={handleAddGradeToProduct}>Adicionar</button> </div> <div className="grades-list-vinculadas mt-3"> {gradesVinculadas.length > 0 ? (<> <label className="form-label-custom small">Grades Vinculadas:</label> <div className="items-list"> {gradesVinculadas.map((grade) => (<span key={grade.id} className="badge me-2 mb-2 p-2" style={{ backgroundColor: '#2185c6', color: 'white' }}> {grade.descricao} ({grade.quantidade_total}) <button type="button" className="btn-close btn-close-white ms-2" style={{ fontSize: '0.5rem' }} onClick={() => handleRemoveVinculadaGrade(grade.id)}></button> </span>))} </div> </>) : (<small className="text-muted">Nenhuma grade vinculada.</small>)} </div> </div>)}
                                {!controlarGrade && tipo === 'Atacado' && (<div className="mt-3"> <small className="text-muted">A quantidade do produto será salva como 1.</small> </div>)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Aba Ficha Técnica (NOVO) --- */}
                <div className={`tab-pane fade ${activeTab === 'fichaTecnica' ? 'show active' : ''}`} id="ficha-pane" role="tabpanel">
                    {isEditing ? (
                        <div className="form-card">
                            <h5 className="form-section-header">Composição do Produto</h5>

                            <div className="mb-3 row">
                                <div className="col-sm-4">
                                    <select
                                        id="selectCorFicha"
                                        className="form-select-custom form-select-custom-sm"
                                        value={corFichaTecnica}
                                        onChange={(e) => setCorFichaTecnica(e.target.value)}
                                        disabled={!controlarCores || colorsList.length === 0}
                                    >
                                        <option value="">Selecione a Cor...</option>
                                        {colorsList.map(corOpt => (
                                            <option key={corOpt} value={corOpt}>{corOpt}</option>
                                        ))}
                                    </select>
                                </div>
                                {!controlarCores && <div className="col-sm-6"><small className="text-danger">Habilite "Controlar Cores" na aba "Dados Cadastrais" e adicione cores.</small></div>}
                            </div>

                            <h6><b>Adicionar Insumo</b></h6>
                            {corFichaTecnica && (
                                <div className="card bg-light p-3 mb-3">
                                    <div className="row g-2 align-items-end">
                                        <div className="col-md-3">
                                            <label htmlFor="selectEtapaFicha" className="form-label-custom form-label-custom-sm">Etapa</label>
                                            <select id="selectEtapaFicha" className="form-select-custom form-select-custom-sm" value={etapaSelecionadaId} onChange={e => setEtapaSelecionadaId(e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {etapasProducao.sort((a, b) => a.sequencia - b.sequencia).map(etapa => (
                                                    <option key={etapa.id} value={etapa.id}>{etapa.sequencia} - {etapa.descricao}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className={showInsumoColor ? "col-md-3" : "col-md-4"}>
                                            <label htmlFor="selectInsumoFicha" className="form-label-custom form-label-custom-sm">Insumo</label>
                                            <select id="selectInsumoFicha" className="form-select-custom form-select-custom-sm"
                                                value={insumoSelecionadoId}
                                                onChange={e => {
                                                    setInsumoSelecionadoId(e.target.value);
                                                    setInsumoCorSelecionada('');
                                                }}>
                                                <option value="">Selecione...</option>
                                                {insumosDisponiveis.map(insumo => (
                                                    <option key={insumo.id} value={insumo.id}>{insumo.codigo} - {insumo.descricao}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {showInsumoColor && (
                                            <div className="col-md-3">
                                                <label htmlFor="selectInsumoCor" className="form-label-custom form-label-custom-sm">Cor do Insumo</label>
                                                <select id="selectInsumoCor" className="form-select-custom form-select-custom-sm"
                                                    value={insumoCorSelecionada}
                                                    onChange={e => setInsumoCorSelecionada(e.target.value)}
                                                >
                                                    <option value="">Selecione a Cor...</option>
                                                    {insumoSelecionado.cores_disponiveis.map(cor => (
                                                        <option key={cor} value={cor}>{cor}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div className={showInsumoColor ? "col-md-1" : "col-md-2"}>
                                            <label htmlFor="inputQtdeInsumo" className="form-label-custom form-label-custom-sm">Qtde</label>
                                            <div className="input-group input-group-sm">
                                                <input type="text" id="inputQtdeInsumo" className="form-control-custom form-control-custom-sm"
                                                    value={quantidadeInsumo}
                                                    onChange={(e) => handleValorChange(e, setQuantidadeInsumo, 4)}
                                                    placeholder="0,0000"
                                                />
                                                {insumoUnidadeSigla && <span className="input-group-text">{insumoUnidadeSigla}</span>}
                                            </div>
                                        </div>

                                        <div className={showInsumoColor ? "col-md-2" : "col-md-3"}>
                                            <button type="button" className="btn btn-success btn-sm w-100" onClick={handleAddInsumoFicha} disabled={loadingFicha}>
                                                {loadingFicha ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <><i className="fas fa-plus me-1"></i> Adicionar</>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <h6 className="mt-4"><b>Itens da Ficha Técnica</b></h6>
                            {loadingFicha && <div className="text-center my-3"><div className="spinner-border spinner-border-sm text-primary" role="status"></div> Carregando...</div>}
                            {!loadingFicha && fichaTecnicaItens.length === 0 && corFichaTecnica && <p className="text-muted">Nenhum insumo adicionado para esta cor.</p>}
                            {!corFichaTecnica && <p className="text-muted">Selecione uma cor para visualizar ou adicionar itens.</p>}

                            {fichaTecnicaItens.length > 0 && (
                                <div className="table-responsive mt-2">
                                    <table className="table table-sm table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Etapa</th>
                                                <th>Insumo</th>
                                                <th className="text-end">Quantidade</th>
                                                <th className="text-end">Custo Unit.</th>
                                                <th className="text-end">Custo Total</th>
                                                <th className="text-center">Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fichaTecnicaItens.map(item => (
                                                <tr key={item.id}>
                                                    <td>{item.etapa_sequencia} - {item.etapa_descricao}</td>

                                                    <td>
                                                        {item.insumo_codigo} - {item.insumo_descricao} - {item.insumo_cor}
                                                    </td>

                                                    <td className="text-end">
                                                        {parseFloat(item.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                                                        {item.insumo_unidade_medida && (
                                                            <span className="text-muted ms-1">{item.insumo_unidade_medida}</span>
                                                        )}
                                                    </td>

                                                    <td className="text-end">{formatarValorMonetario(item.custo_unitario)}</td>

                                                    <td className="text-end">{formatarValorMonetario(item.custo_total)}</td>

                                                    <td className="text-center">
                                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveInsumoFicha(item.id)} disabled={loadingFicha} title="Remover Insumo">
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="table-light fw-bold">
                                                <td colSpan="4" className="text-end">Custo Total da Ficha ({corFichaTecnica}):</td>
                                                <td className="text-end">{formatarValorMonetario(custoTotalFicha)}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-muted">A Ficha Técnica só está disponível ao editar um produto.</p>
                    )}
                </div>

                {/* --- Aba Fornecedores (NOVO) --- */}
                <div className={`tab-pane fade ${activeTab === 'fornecedores' ? 'show active' : ''}`} id="fornecedores-pane" role="tabpanel">
                    <div className="form-card">
                        <h5 className="form-section-header">Fornecedores Vinculados</h5>
                        <p className="text-muted small">Adicione outros fornecedores e seus respectivos custos para este produto.</p>

                        <div className="form-card form-card-action">
                            <h5 className="form-section-header">Adicionar Fornecedor</h5>
                            <div className="row g-3 align-items-end">
                                <div className="col-md-4">
                                    <label className="form-label-custom">Fornecedor</label>
                                    <select
                                        className="form-select-custom"
                                        value={fornecedorExtraId}
                                        onChange={(e) => setFornecedorExtraId(e.target.value)}
                                    >
                                        <option value="">Selecione...</option>
                                        {fornecedores.map(f => (
                                            <option key={f.id} value={f.id}>{f.nome}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label-custom">Valor de Compra</label>
                                    <input
                                        type="text"
                                        className="form-control-custom"
                                        value={String(valorCompraExtra).replace('.', ',')}
                                        onChange={(e) => handleValorChange(e, setValorCompraExtra)}
                                        placeholder="0,00"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label-custom">Valor de Venda</label>
                                    <input
                                        type="text"
                                        className="form-control-custom"
                                        value={String(valorVendaExtra).replace('.', ',')}
                                        onChange={(e) => handleValorChange(e, setValorVendaExtra)}
                                        placeholder="0,00"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button type="button" className="btn btn-primary-custom w-100" onClick={handleAddFornecedor}>
                                        <i className="fas fa-plus me-2"></i> Adicionar
                                    </button>
                                </div>
                            </div>
                        </div>

                        {fornecedoresVinculados.length > 0 ? (
                            <div className="form-card mt-3">
                                <h5 className="form-section-header">Fornecedores Vinculados</h5>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Fornecedor</th>
                                                <th className="text-end" style={{ width: '150px' }}>Valor de Compra</th>
                                                <th className="text-end" style={{ width: '150px' }}>Valor de Venda</th>
                                                <th className="text-center" style={{ width: '100px' }}>Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fornecedoresVinculados.map((item, idx) => (
                                                <tr key={`${item.fornecedor_id}-${idx}`}>
                                                    <td>{item.nome || item.nome_fornecedor}</td>
                                                    <td className="text-end">{formatarValorMonetario(item.valor_compra)}</td>
                                                    <td className="text-end">{formatarValorMonetario(item.valor_venda || 0)}</td>
                                                    <td className="text-center">
                                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveFornecedor(item.fornecedor_id)}>
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="alert alert-light text-center mt-3">Nenhum fornecedor extra vinculado.</div>
                        )}
                    </div>
                </div>

            </div>

            {/* Botões de Ação Principais (Fixed Footer) */}
            <div className="form-actions-bar">
                <Link to='/app/produtos' className="btn-secondary-custom">Cancelar</Link>
                <button
                    type="submit"
                    className={`btn-primary-custom ${(!isDirty && isEditing) ? 'disabled-look' : ''}`}
                    disabled={loadingSubmit || loadingFicha || (isEditing && !isDirty)}
                    style={{ opacity: (isEditing && !isDirty) ? 0.6 : 1, cursor: (isEditing && !isDirty) ? 'not-allowed' : 'pointer' }}
                >
                    {loadingSubmit ? (isEditing ? 'Atualizando...' : 'Salvando...') : (isEditing ? (!isDirty ? 'Salvo' : 'Salvar Alterações') : 'Salvar Produto')}
                </button>
            </div>
        </form>
    );
}

export default ProdutoForm;